import { ComponentConfig, FieldLabel } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import { PaymentController } from './PaymentController';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { CustomizeProps } from '../../interface/commonInterface';
import { DismissError } from './DismissError';
import { IFieldOption, IPaymentField } from '../../interface/formInterface';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { termsAndSubFields, TermsAndSubProps } from './termsAndSubFields';
import { getQuillEditorText } from '../../utils/helper';
import AddressHelper from '../address/AddressHelper';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
  PaddingType,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../puck/reusable-props/inputfield-height';
import WithAccordion from '../customComponents/CustomAccordion';
import BackgroungColorComponent from '../customComponents/BackgroungColorComponent';
import '../../components/text-editor/style.scss';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import PaymentHelper from './PaymentHelper';
import { Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { defaultBorderProps, toolTipInfo } from '../../utils/constants';
import { marginProps, MarginType } from '../../puck/reusable-props/NewMargin';
import { IAsset } from '../../interface/apiServiceInterface';
import { AxiosInstance } from 'axios';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../puck/reusable-props/widthAndHeight';

import InputField from '../../../components/common/input-field';
import { ImageSelector } from '../../../components/common/image-selector/ImageSelector';
import JustifyContent, {
  IJustifyContentProps,
} from '../blocks/Container/justify-content';
import NewMarginFiled from '../../puck/reusable-props/newMarginField';
import PaddingComponent from '../customComponents/PaddingComponent';
import QuilHelper from '../../puck/reusable-props/quilHelper';
import Select from '../../../components/common/select';
import { outlineProps } from '../../puck/reusable-props/Outline';

export type PaymentProps = {
  subText: CustomizeProps;
  isShowBillingAddress?: boolean;
  customCheckboxText: string;
  isShowNameOnCard: boolean;
  checkboxColor?: string;
  customCheckbox?: {
    checkedBorderColor?: string;
    uncheckedBorderColor?: string;
    checkboxshape?: string;
    checkboxSize?: string;
    enrollmentStyle?: string;
    checkMarkColor?: string;
    checkedToggleColor?: string;
    uncheckedToggleColor?: string;
    toggleSize?: string;
    checkMarkSize?: string;
  };
  isShowSubText?: boolean;
  useNewExpCvvLayout?: boolean;
  paymentPlaceholder: IPaymentField;
  option?: IFieldOption;
  showSVG: {
    showAMEX: boolean;
    showMastercard: boolean;
    showVISA: boolean;
    showDiscover: boolean;
    background: {
      color: string;
      isInherit: boolean;
    };
  };
  headerLogoCustomization: {
    imageSize: WidthAndHeightSubProps;
    showHeaderLogo: boolean;
    justifyContent: IJustifyContentProps;
    gap: string;
    imageFile: IAsset;
    color: string;
    margin: MarginType;
    padding: PaddingType;
  };
  svgCustomization: {
    imageSize: WidthAndHeightSubProps;
    gap: string;
  };
  background: {
    color: string;
    isInherit: boolean;
  };
  header: {
    background: {
      color: string;
      isInherit: boolean;
    };
    borderColor: string;
  };
  billingAddressLabel: {
    text: string;
    padding: PaddingType;
  };

  border: IBorderValueProps;
} & MarginProps &
  PaddingProps &
  fieldHeightProps &
  TermsAndSubProps;
export interface CustomerProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const Payment = ({
  useAppSelector,
  useAppDispatch,
  baseService,
}: ReduxSelectorProps &
  ReduxDispatchProps &
  CustomerProps): ComponentConfig<PaymentProps> => {
  return {
    label: (
      <CustomTooltip title="Payment">
        <span>Payment</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      subText: {
        type: 'custom',
        label: 'Sub Text',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            showComponent={true}
            value={value}
            onChange={onChange}
            title={field?.label}
          />
        ),
      },
      headerLogoCustomization: {
        type: 'custom',
        label: 'Header Section Customization',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Header Section Customization">
              <FieldLabel
                className="grid gap-4"
                el="div"
                label={
                  (
                    <CustomTooltip title="Image">
                      <span>Image</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }>
                <ImageSelector
                  selectedImage={value?.imageFile}
                  handleSubmit={image => {
                    onChange({
                      ...value,
                      imageFile: image,
                    });
                  }}
                  baseService={baseService}
                />
              </FieldLabel>
              <WidthAndHeight
                value={value?.imageSize}
                onChange={val => {
                  onChange({
                    ...value,
                    imageSize: val,
                  });
                }}
                title={'Height & Width'}
              />
              <ColorPicker
                label="Hover Color"
                tooltip="color"
                value={value?.color}
                onChange={color => {
                  onChange({
                    ...value,
                    color,
                  });
                }}
              />
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Gap</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.gap}
                  onChange={e => {
                    let newValue = e.replace(/[^0-9%]/g, '');

                    // Ensure that only one '%' symbol remains at the end
                    if (newValue?.includes('%')) {
                      newValue =
                        newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                    }

                    // If the string is empty or contains only whitespace, set to 'auto'
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    // If the value includes both digits and '%', clean up any non-digit/% characters
                    if (/\d/.test(newValue) && /%/.test(newValue)) {
                      newValue = newValue.replace(/[^0-9%]/g, '');
                    }

                    // Default to 'auto' if the value is still empty
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    onChange({ ...value, gap: newValue });
                  }}
                />
              </FieldLabel>
              <JustifyContent
                value={value?.justifyContent}
                onChange={val => {
                  onChange({
                    ...value,
                    justifyContent: val,
                  });
                }}
                title={'Justify Content'}
              />

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Show Header Image">
                      <span>Show Image</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }>
                <Switch
                  className="flex justify-between items-center mb-3"
                  checked={value?.showHeaderLogo}
                  onChange={e => {
                    const selectedValue = e.target.checked;
                    onChange({
                      ...value,
                      showHeaderLogo: selectedValue,
                    });
                  }}
                />
              </FieldLabel>
              <NewMarginFiled
                value={value.margin}
                onChange={margin => onChange({ ...value, margin })}
                title="Margin"
              />
              <PaddingComponent
                value={value.padding}
                onChange={padding => onChange({ ...value, padding })}
                title="Padding"
              />
            </WithAccordion>
          );
        },
      },
      useNewExpCvvLayout: {
        type: 'radio',
        label: 'Side-By-Side Exp & CVV (Mobile)',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      paymentPlaceholder: {
        type: 'custom',
        label: 'Place Holder',
        render: ({ value, onChange }) => {
          return value ? (
            <PaymentHelper
              value={value}
              onChange={onChange}
              name={`PlaceHolder Form`}
            />
          ) : (
            <></>
          );
        },
      },
      isShowSubText: {
        type: 'radio',
        label: 'Show Sub Text',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      ...inputFieldHeightProps(),
      ...termsAndSubFields,

      header: {
        type: 'custom',
        label: 'Header',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title={'Header'}>
              <BackgroungColorComponent
                value={value?.background}
                onChange={background => {
                  onChange({ ...value, background });
                }}
              />
              <ColorPicker
                label="Border Color"
                tooltip="borderColor"
                value={value?.borderColor}
                onChange={color => {
                  onChange({
                    ...value,
                    borderColor: color,
                  });
                }}
              />
            </WithAccordion>
          );
        },
      },
      isShowBillingAddress: {
        type: 'radio',
        label: 'Show Billing Address',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      customCheckboxText: {
        ...QuillTextEditor('Custom Checkbox Text').content,
      },
      isShowNameOnCard: {
        type: 'radio',
        label: 'Show Name on card',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      checkboxColor: {
        type: 'custom',
        label: 'Checkbox Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      billingAddressLabel: {
        type: 'custom',
        label: 'Billing Address Label',
        render: ({ value, onChange }) => (
          <WithAccordion title="Billing Address Label">
            <QuilHelper
              value={value.text}
              onChange={val => {
                onChange({
                  ...value,
                  text: val,
                });
              }}
              enableLink={true}
              id={1}
            />

            <PaddingComponent
              value={value?.padding}
              onChange={padding => onChange({ ...value, padding })}
              title="Padding"
            />
          </WithAccordion>
        ),
      },
      customCheckbox: {
        type: 'custom',
        label: 'Custom Checkbox',
        render: ({ value, onChange }) => {
          const options = [
            { label: 'Square', value: 'rounded' },
            { label: 'Circle', value: 'rounded-full' },
          ];
          return (
            <WithAccordion title="Custom Checkbox">
              <>
                <div>
                  <ColorPicker
                    value={value?.checkedBorderColor}
                    onChange={color =>
                      onChange({ ...(value || {}), checkedBorderColor: color })
                    }
                    label="Checked Border Color"
                    tooltip="Checked Border Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.uncheckedBorderColor}
                    onChange={color =>
                      onChange({
                        ...(value || {}),
                        uncheckedBorderColor: color,
                      })
                    }
                    label="Unchecked Border Color"
                    tooltip="Unchecked Border Color"
                  />
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Shape' as keyof typeof toolTipInfo}>
                          <span>Checkbox Shape</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <div className="w-full p-2">
                      <ToggleButtonGroup
                        value={value?.checkboxshape}
                        exclusive
                        className="w-full"
                        onChange={(_e, selectValue) => {
                          selectValue &&
                            onChange({ ...value, checkboxshape: selectValue });
                        }}>
                        {options.map((option, index: number) => (
                          <ToggleButton
                            key={index}
                            size="small"
                            value={option.value}
                            disableRipple
                            aria-label={option.value}
                            className="flex-1">
                            {option.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </div>
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Size' as keyof typeof toolTipInfo}>
                          <span>Checkbox Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkboxSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkboxSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter checkbox size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="EnrollementStyle">
                          <span>Enrollement Style</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                  />
                  <Select
                    value={value?.enrollmentStyle}
                    onChange={val =>
                      onChange({ ...value, enrollmentStyle: val })
                    }
                    options={[
                      {
                        label: 'Checkbox',
                        value: 'checkbox',
                      },
                      {
                        label: 'Toggle',
                        value: 'toggle',
                      },
                    ]}
                  />
                </div>
                <ColorPicker
                  value={value?.checkMarkColor || '#ffffff'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkMarkColor: color,
                    })
                  }
                  label={'Check Mark Color'}
                  tooltip={'Check Mark Color'}
                />
                <ColorPicker
                  value={value?.checkedToggleColor || '#4f46e5'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkedToggleColor: color,
                    })
                  }
                  label={'Checked Toggle Color'}
                  tooltip={'Checked Toggle Color'}
                />
                <ColorPicker
                  value={value?.uncheckedToggleColor || '#d1d5db'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      uncheckedToggleColor: color,
                    })
                  }
                  label={'Unchecked Toggle Color'}
                  tooltip={'Unchecked Toggle Color'}
                />
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Toggle Size' as keyof typeof toolTipInfo}>
                          <span>Toggle Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.toggleSize || 20}
                      onChange={e =>
                        onChange({
                          ...value,
                          toggleSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Check Mark Size' as keyof typeof toolTipInfo}>
                          <span>Check Mark Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkMarkSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkMarkSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter check mark size"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      option: {
        type: 'custom',
        label: 'Options',
        render: ({ value, onChange }) => {
          return value ? (
            <AddressHelper
              value={value}
              onChange={onChange}
              name={`Billing Form`}
            />
          ) : (
            <></>
          );
        },
      },
      showSVG: {
        type: 'custom',
        label: 'Show SVG',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title={'Show SVG'}>
              <BackgroungColorComponent
                value={value?.background}
                onChange={background => {
                  onChange({ ...value, background });
                }}
              />
              <div className="flex justify-between items-center mb-3">
                <label>Show AMEX</label>
                <Switch
                  className="flex justify-between items-center mb-3"
                  checked={value?.showAMEX}
                  onChange={e => {
                    const selectedValue = e.target.checked;
                    onChange({
                      ...value,
                      showAMEX: selectedValue,
                    });
                  }}
                />
              </div>

              <div className="flex justify-between items-center mb-3">
                <label>Show Mastercard</label>
                <Switch
                  className="flex justify-between items-center mb-3"
                  checked={value?.showMastercard}
                  onChange={e => {
                    const selectedValue = e.target.checked;
                    onChange({
                      ...value,
                      showMastercard: selectedValue,
                    });
                  }}
                />
              </div>

              <div className="flex justify-between items-center mb-3">
                <label>Show VISA</label>
                <Switch
                  className="flex justify-between items-center mb-3"
                  checked={value?.showVISA}
                  onChange={e => {
                    const selectedValue = e.target.checked;
                    onChange({
                      ...value,
                      showVISA: selectedValue,
                    });
                  }}
                />
              </div>
              <div className="flex justify-between items-center mb-3">
                <label>Show Discover</label>
                <Switch
                  className="flex justify-between items-center mb-3"
                  checked={value?.showDiscover}
                  onChange={e => {
                    const selectedValue = e.target.checked;
                    onChange({
                      ...value,
                      showDiscover: selectedValue,
                    });
                  }}
                />
              </div>
            </WithAccordion>
          );
        },
      },
      svgCustomization: {
        type: 'custom',
        label: 'SVG Customization',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title={'SVG Customization'}>
              <WidthAndHeight
                value={value?.imageSize}
                onChange={val => {
                  onChange({
                    ...value,
                    imageSize: val,
                  });
                }}
                title={'Height & Width'}
              />
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Gap</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.gap}
                  onChange={e => {
                    let newValue = e.replace(/[^0-9%]/g, '');

                    // Ensure that only one '%' symbol remains at the end
                    if (newValue?.includes('%')) {
                      newValue =
                        newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                    }

                    // If the string is empty or contains only whitespace, set to 'auto'
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    // If the value includes both digits and '%', clean up any non-digit/% characters
                    if (/\d/.test(newValue) && /%/.test(newValue)) {
                      newValue = newValue.replace(/[^0-9%]/g, '');
                    }

                    // Default to 'auto' if the value is still empty
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    onChange({ ...value, gap: newValue });
                  }}
                />
              </FieldLabel>
            </WithAccordion>
          );
        },
      },
    },
    defaultProps: {
      svgCustomization: {
        imageSize: {
          deviceType: 'desktop',
          height: {
            desktop: '28',
            mobile: '28',
            tablet: '28',
          },
          maxWidth: {
            desktop: '35',
            mobile: '35',
            tablet: '35',
          },
        },
        gap: '0',
      },
      headerLogoCustomization: {
        imageFile: {
          storeID: '',
          fileName: 'logo',
          imageUrl: '',
        },
        imageSize: {
          deviceType: 'desktop',
          height: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
          maxWidth: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
        },
        justifyContent: {
          deviceType: 'desktop',
          justify: {
            desktop: 'justify-start',
            mobile: 'justify-start',
            tablet: 'justify-start',
          },
        },
        showHeaderLogo: false,
        color: '#f2f2f2',
        gap: '0',
        margin: marginProps.margin,
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
          paddingLeft: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
          paddingRight: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
          paddingTop: {
            desktop: '20',
            mobile: '20',
            tablet: '20',
          },
        },
      },
      useNewExpCvvLayout: false,
      subText: {
        weight: {
          mobile: 'font-normal',
          tablet: 'font-normal',
          desktop: 'font-normal',
        },
        deviceType: 'desktop',
        text: 'All transactions are secured and encrypted',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        showComponent: {
          mobile: true,
          tablet: true,
          desktop: true,
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      paymentPlaceholder: {
        Name: {
          headers: {
            hide: false,
            text: 'Name on Card',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Name on Card',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
        },
        Number: {
          headers: {
            hide: false,
            text: 'Credit Card Number',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Credit Card Number',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
        },
        Exp: {
          headers: {
            hide: false,
            text: 'Expiration date (MM/YY)',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Expiration date (MM/YY)',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
        },
        CVV: {
          headers: {
            hide: false,
            text: 'Security code',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Security code',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
        },
        Title: {
          headers: {
            hide: false,
            text: 'Title',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Credit Card',
          color: '#000000',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          fontFamily: 'Arial, sans-serif',
          letterSpace: '0',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
        },
      },
      showSVG: {
        showAMEX: false,
        showMastercard: true,
        showVISA: true,
        showDiscover: false,
        background: {
          color: '#ffffff',
          isInherit: false,
        },
      },
      option: {
        FirstName: {
          header: {
            hide: false,
            text: 'First Name',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'First Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'First Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        LastName: {
          header: {
            hide: false,
            text: 'Last Name',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Last Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'Last Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        Address1: {
          header: {
            hide: false,
            text: 'Address',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Address',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'Address is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        Address2: {
          header: {
            hide: false,
            text: 'Address 2',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Apartment, suite, etc. (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        City: {
          header: {
            hide: false,
            text: 'City',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'City',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'City is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        CountryCode: {
          header: {
            hide: false,
            text: 'Country',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select',
          showOnlyCountry: [],
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'Country is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        ProvinceCode: {
          header: {
            hide: false,
            text: 'State / Province',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select State / Province',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        Organization: {
          header: {
            hide: false,
            text: 'Company',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Company (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
        // Phone: {
        //   placeholder: 'Phone Number',
        //   defaultPhoneCode: '',
        //   show: true,
        //   border: {
        //     borderTopLeft: '6',
        //     borderTopRight: '6',
        //     borderBottomLeft: '6',
        //     borderBottomRight: '6',
        //   },
        //   requiredValidation: true,
        //   validationText: 'Phone Number is required',
        //   color: '#808080',
        //   fontSize: '14',
        //   fontWeight: '300',
        //   lineHeight: '1.5',
        //   textAlign: 'left',
        //   fontFamily: 'sans-serif',
        //   letterSpace: '0',
        // },
        Zip: {
          header: {
            hide: false,
            text: 'Postal code',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Postal code',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          ...outlineProps,
          requiredValidation: true,
          validationText: 'Postal code is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
        },
      },
      billingAddressLabel: {
        text: '<p style="line-height: 1.5;"><span style="font-size: 18px; font-weight: 600;">Billing address</span></p>',
        ...paddingProps,
      },
      isShowBillingAddress: false,
      customCheckboxText:
        '<p style="line-height: 1.5;"><span style="font-size: 16px; color: rgb(107, 114, 128);">Use shipping address as billing address</span></p>',
      isShowNameOnCard: false,
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        rowGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        colGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        deviceType: 'desktop',
      },
      isShowSubText: true,
      termsAndSub: {
        isShowTermsAndSub: false,
        terms:
          '<p style="line-height: 1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
        moreTerms:
          '<p style="line-height: 1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
        defaultCollapse: false,
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...paddingProps,
      ...borderProps,

      background: {
        color: '#ffffff',
        isInherit: false,
      },
      checkboxColor: '#4f46e5',
      customCheckbox: {
        checkedBorderColor: '#4f46e5',
        uncheckedBorderColor: '#d1d5db',
        checkboxshape: 'rounded',
        checkboxSize: '16',
        enrollmentStyle: 'checkbox',
        checkMarkColor: '#ffffff',
        checkedToggleColor: '#4f46e5',
        uncheckedToggleColor: '#d1d5db',
        toggleSize: '20',
        checkMarkSize: '16',
      },
      header: {
        background: {
          color: '#F2F2F2',
          isInherit: false,
        },
        borderColor: '#ccc',
      },
    },
    render: ({
      margin,
      termsAndSub,
      isShowBillingAddress,
      customCheckboxText,
      useNewExpCvvLayout,
      isShowSubText,
      subText,
      paymentPlaceholder,
      background,
      heightField,
      option,
      editMode,
      isShowNameOnCard,
      headerLogoCustomization,
      padding,
      border,
      checkboxColor,
      customCheckbox,
      header,
      showSVG,
      svgCustomization,
      billingAddressLabel,
    }) => {
      const state = useAppSelector(state => state);
      const newSubText = {
        ...subText,
        text: getQuillEditorText(subText?.text, state),
      };
      return (
        <>
          <PaymentController
            fields={{
              termsAndSub,
              margin,
              isShowSubText,
              useNewExpCvvLayout,
              isShowBillingAddress,
              customCheckboxText,
              isShowNameOnCard,
              subText: newSubText,
              paymentPlaceholder,
              heightField,
              option,
              background,
              padding,
              border,
              checkboxColor,
              customCheckbox,
              header,
              showSVG,
              headerLogoCustomization,
              svgCustomization,
              billingAddressLabel,
            }}
            editMode={editMode}
            useAppSelector={useAppSelector}
            useAppDispatch={useAppDispatch}
          />
          <DismissError
            useAppSelector={useAppSelector}
            useAppDispatch={useAppDispatch}
          />
        </>
      );
    },
  };
};
